import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import { monogram } from "./Ui";
import type { Client } from "../types";

/**
 * The client name at the top of a client's sidebar. Clicking it opens a
 * searchable list (a native select can't be searched past the first letter).
 * The list opens in the sidebar's flow, not as a floating menu, so the
 * scrolling nav can't clip it.
 */
export default function ClientSwitcher({
  clients,
  current,
  onPick,
}: {
  clients: Client[];
  current: Client;
  onPick: (id: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [active, setActive] = useState(0);
  const box = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const listId = useId();
  const term = search.trim().toLowerCase();
  const matches = term ? clients.filter((c) => c.name.toLowerCase().includes(term)) : clients;

  const close = (refocus = true) => {
    setOpen(false);
    setSearch("");
    if (refocus) button.current?.focus();
  };
  const pick = (id: number) => {
    close();
    if (id !== current.id) onPick(id);
  };

  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => {
      if (!box.current?.contains(e.target as Node)) close(false);
    };
    document.addEventListener("pointerdown", away);
    return () => document.removeEventListener("pointerdown", away);
  }, [open]);
  // Keep the highlighted row in view while arrowing through a long list.
  useEffect(() => {
    if (open) document.getElementById(`${listId}-${active}`)?.scrollIntoView?.({ block: "nearest" });
  }, [open, active, listId]);

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      close();
    } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!matches.length) return;
      const step = e.key === "ArrowDown" ? 1 : -1;
      setActive((i) => (i + step + matches.length) % matches.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (matches[active]) pick(matches[active].id);
    }
  };

  return (
    <div className={`client-switcher${open ? " open" : ""}`} ref={box}>
      <button
        type="button"
        ref={button}
        className="client-nav-head"
        aria-label={`Switch client, current ${current.name}`}
        aria-expanded={open}
        onClick={() => {
          if (open) return close();
          setActive(Math.max(0, clients.findIndex((c) => c.id === current.id)));
          setOpen(true);
        }}
      >
        <span className="client-mark" aria-hidden="true">
          {monogram(current.name)}
        </span>
        <span className="client-nav-name">{current.name}</span>
        <ChevronsUpDown aria-hidden="true" />
      </button>
      {open && (
        <div className="client-switcher-panel">
          <label className="client-nav-search">
            <Search aria-hidden="true" />
            <input
              autoFocus
              role="combobox"
              aria-label="Find a client"
              aria-expanded="true"
              aria-controls={listId}
              aria-activedescendant={matches[active] ? `${listId}-${active}` : undefined}
              placeholder="Find a client…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setActive(0);
              }}
              onKeyDown={onKey}
            />
          </label>
          <ul className="client-switcher-list" id={listId} role="listbox" aria-label="Clients">
            {matches.map((c, i) => {
              const inactive = c.status && c.status !== "active" ? c.status.charAt(0).toUpperCase() + c.status.slice(1) : "";
              return (
                <li
                  key={c.id}
                  id={`${listId}-${i}`}
                  role="option"
                  aria-selected={c.id === current.id}
                  className={`client-switcher-option${i === active ? " active" : ""}${inactive ? " inactive" : ""}`}
                  // Keep focus in the search box; the click still picks.
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseMove={() => setActive(i)}
                  onClick={() => pick(c.id)}
                >
                  <span className="client-row-name">{c.name}</span>
                  {inactive && <span className="client-row-status">{inactive}</span>}
                  {c.id === current.id && <Check aria-hidden="true" />}
                </li>
              );
            })}
          </ul>
          {!matches.length && (
            <p className="client-nav-note">
              No matching clients.{" "}
              <button type="button" className="text-button" onClick={() => setSearch("")}>
                Clear search
              </button>
            </p>
          )}
        </div>
      )}
    </div>
  );
}
